import { useEffect, useState, FC } from 'react';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';

import { Box, Alert, CircularProgress, Typography, SxProps, Theme } from '@mui/material';
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from '@stripe/react-stripe-js';
import { loadStripe, Stripe } from '@stripe/stripe-js';

const STRINGS = appStrings.modules.subscription.embeddedCheckout;

interface EmbeddedCheckoutProps {
  clientSecret: string;
  onSuccess: () => void;
  onError: (error: string) => void;
  handleBackToPlans: () => void;
  showBackToPlans?: boolean;
  isConstrainedLayout?: boolean;
}

export const EmbeddedCheckoutComponent: FC<EmbeddedCheckoutProps> = ({
  clientSecret,
  onSuccess,
  onError,
  handleBackToPlans,
  showBackToPlans = true,
  isConstrainedLayout = false,
}) => {
  const [stripe, setStripe] = useState<Stripe | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const initializeStripe = async () => {
      try {
        const stripeInstance = await loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '');

        setStripe(stripeInstance);
      } catch (err) {
        console.error('Failed to load Stripe:', err);
        setError('Failed to initialize payment system');
        onError('Failed to initialize payment system');
      } finally {
        setLoading(false);
      }
    };

    initializeStripe();
  }, [onError]);

  if (loading) {
    return (
      <Box sx={loadingContainerSx}>
        <CircularProgress size={24} />
        <Typography variant="text-md-bold">{STRINGS.initializing}</Typography>
      </Box>
    );
  }

  if (error || !stripe) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error || 'Failed to load payment system'}
      </Alert>
    );
  }

  return (
    <Box sx={embeddedCheckoutContainerSx}>
      {showBackToPlans && (
        <Box sx={headerContainerSx}>
          <Box sx={backToPlansContainerSx}>
            <Typography
              id="subscription-back-to-plans-button"
              variant="text-xs-medium"
              sx={backToPlansSx}
              onClick={handleBackToPlans}
            >
              {STRINGS.backToPlans}
            </Typography>
          </Box>
        </Box>
      )}
      <Box sx={checkoutContainerSx(isConstrainedLayout)}>
        <EmbeddedCheckoutProvider
          stripe={stripe}
          options={{
            clientSecret,
            onComplete: () => onSuccess(),
          }}
        >
          <EmbeddedCheckout />
        </EmbeddedCheckoutProvider>
      </Box>
    </Box>
  );
};

const loadingContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-md'],
  justifyContent: 'center',
  py: backrs.spacing['spacing-xl'],
});

const checkoutContainerSx = (isConstrainedLayout: boolean): SxProps<Theme> => ({
  width: !isConstrainedLayout
    ? '100vw'
    : {
        xs: '100vw',
        sm: '60vw',
        md: '70vw',
        lg: '90vw',
      },
  maxWidth: isConstrainedLayout ? '1100px' : '1240px',
  overflowY: 'scroll',
  overflowX: 'hidden',
});

const embeddedCheckoutContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-lg'],
  alignItems: 'center',
  mb: backrs.spacing['spacing-2xl'],
});

const backToPlansContainerSx: SxProps<Theme> = ({ backrs }) => ({
  mt: backrs.spacing['spacing-md'],
  textAlign: 'center',
});

const headerContainerSx: SxProps<Theme> = ({ backrs }) => ({
  position: 'relative',
  width: '100%',
  textAlign: 'center',
  mb: backrs.spacing['spacing-lg'],
  mt: backrs.spacing['spacing-3xl'],
});

const backToPlansSx: SxProps<Theme> = ({ backrs }) => ({
  position: 'absolute',
  left: {
    xs: backrs.spacing['spacing-md'],
    sm: backrs.spacing['spacing-7xl'],
  },
  bottom: backrs.spacing['spacing-3xl'],
  color: backrs.colors['Colors/Text/text-secondary'],
  cursor: 'pointer',
  textDecoration: 'none',
  pb: backrs.spacing['spacing-md'],
});
